import React, { useState } from 'react';
import { Slide, ToastContainer } from 'react-toastify';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InfoTooltip from '../../info-tooltip/info-tooltip';
import { isHeaderOnlyConsiderationType } from '../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import {
    ConsiderationAmounts,
    ConsiderationField,
    ConsiderationLedger as ConsiderationLedgerModel,
    ConsiderationLedgerRow,
    ConsiderationSection,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import {
    EM_DASH,
    considerationTreatmentName,
    considerationTypeName,
    formatMoney,
} from '../../../utils/flowOfFundsFormat';
import { toWireValue } from '../../../utils/flow-of-funds/consideration-field-format';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { ConsiderationDetailTables } from './ConsiderationDetailTables';
import { EditableConsiderationCell } from './EditableConsiderationCell';
import {
    CONSIDERATION_AUTOSAVE_TOAST_CONTAINER_ID,
    ConsiderationAutoSaveProvider,
    useConsiderationAutoSaveReporterState,
} from './ConsiderationAutoSaveContext';
import { ConsiderationAutosaveQueueProvider } from './ConsiderationAutosaveQueueContext';
import { useConsiderationAutosave } from './useConsiderationAutosave';
import { ReadOnlyApproval } from '../ApprovalControl/ApprovalControl';
import '../../AutoSaveToast/AutoSaveToast.scss';
import '../../../containers/ConnectMarketplaceDashboard/LoiComparisonTab/LoiComparisonSummary/LoiComparisonSummary.scss';
import './ConsiderationLedger.scss';

type AmountKey = keyof ConsiderationAmounts;

const FEE_BREAKDOWN_TOOLTIP = 'Fee breakdown available';
const PERCENT_OF_TOTAL_LABEL = '% of Total';
const BUYER_APPROVAL_LABEL = 'Buyer Approval';
const SELLER_APPROVAL_LABEL = 'Seller Approval';

type PercentKey = 'face' | 'fair';

interface AmountColumn {
    key: AmountKey;
    header: string;
    percentKey?: PercentKey;
    editableField?: ConsiderationField;
}

const FACE_VALUE_COLUMN: AmountColumn = {
    key: 'faceValue',
    header: 'Face Values',
    percentKey: 'face',
    editableField: ConsiderationField.FaceValue,
};
const LIABILITY_PAYOFFS_COLUMN: AmountColumn = {
    key: 'liabilityPayoffs',
    header: 'Liability Payoffs',
};
const EMPLOYEE_PORTION_COLUMN: AmountColumn = {
    key: 'employeePortion',
    header: 'Employee Portion',
    editableField: ConsiderationField.EmployeePortion,
};

const FULL_AMOUNT_COLUMNS: AmountColumn[] = [
    FACE_VALUE_COLUMN,
    { key: 'fairValue', header: 'Fair Value', percentKey: 'fair' },
    { key: 'projectedCashFlow', header: 'Projected Cash Flow' },
    { key: 'projectedFees', header: 'Projected Fees' },
    { key: 'netOfFees', header: 'Net of Fees' },
    LIABILITY_PAYOFFS_COLUMN,
    EMPLOYEE_PORTION_COLUMN,
    { key: 'netToOwner', header: 'Net to Owner' },
];

const BUYER_AMOUNT_COLUMNS: AmountColumn[] = [
    FACE_VALUE_COLUMN,
    LIABILITY_PAYOFFS_COLUMN,
    EMPLOYEE_PORTION_COLUMN,
];

const formatPercentOfTotal = (
    value: number | null | undefined,
    total: number | null | undefined
): string => {
    if (value == null || total == null || total === 0) {
        return EM_DASH;
    }
    const percentOfTotal = (value / total) * 100;
    return `${percentOfTotal.toFixed(1).replace(/\.0$/, '')}%`;
};

interface AmountCellProps {
    value: number | null;
    feesNotApplicable?: boolean;
    tooltipId?: string;
}

const AmountCell = ({ value, feesNotApplicable, tooltipId }: AmountCellProps) => {
    const text = feesNotApplicable ? 'NA' : formatMoney(value);
    const isMuted = text === EM_DASH || text === 'NA';

    return (
        <td className={`cell-value${isMuted ? ' ff-ledger__num--muted' : ''}`}>
            <span>{text}</span>
            {tooltipId && <InfoTooltip text={FEE_BREAKDOWN_TOOLTIP} idText={tooltipId} />}
        </td>
    );
};

const PercentCell = ({
    visible,
    value,
    total,
}: {
    visible: boolean;
    value: number | null;
    total: number | null;
}) =>
    visible ? (
        <td className="cell-value">{formatPercentOfTotal(value, total)}</td>
    ) : (
        <td className="percent-expander-col" />
    );

const PercentToggleHeader = ({
    pctKey,
    visible,
    onToggle,
}: {
    pctKey: PercentKey;
    visible: boolean;
    onToggle: (key: PercentKey) => void;
}) =>
    visible ? (
        <th className="sub-col-header">
            <button
                type="button"
                className="percent-toggle-btn"
                onClick={() => onToggle(pctKey)}
                aria-label="Collapse % column">
                <FontAwesomeIcon icon={faMinus} />
            </button>
            {PERCENT_OF_TOTAL_LABEL}
        </th>
    ) : (
        <th className="percent-expander-col">
            <button
                type="button"
                className="percent-toggle-btn"
                onClick={() => onToggle(pctKey)}
                aria-label="Expand % column">
                <FontAwesomeIcon icon={faPlus} />
            </button>
        </th>
    );

interface LedgerDataRowProps {
    row: ConsiderationAmounts;
    total: ConsiderationAmounts;
    columns: AmountColumn[];
    visiblePercents: Record<PercentKey, boolean>;
    feesNotApplicable?: boolean;
    hasFeeBreakdown?: boolean;
    tooltipId: string;
    showTiming: boolean;
    timing: React.ReactNode;
    isTotal?: boolean;
    ledgerRow?: ConsiderationLedgerRow;
    isHeaderOnly?: boolean;
    engagementId: number;
}

const LedgerDataRow = ({
    row,
    total,
    columns,
    visiblePercents,
    feesNotApplicable,
    hasFeeBreakdown,
    tooltipId,
    showTiming,
    timing,
    isTotal,
    ledgerRow,
    isHeaderOnly,
    engagementId,
}: LedgerDataRowProps) => {
    const canEditRow = !!ledgerRow && !isTotal && !isHeaderOnly;

    return (
        <tr className={isTotal ? 'total-row' : undefined}>
            {columns.map((column) => {
                const isProjectedFees = column.key === 'projectedFees';
                const canEditColumn =
                    canEditRow && column.editableField !== undefined && !!ledgerRow;
                return (
                    <React.Fragment key={column.key}>
                        {canEditColumn && ledgerRow ? (
                            <EditableConsiderationCell
                                field={column.editableField as ConsiderationField}
                                wireValue={
                                    toWireValue(
                                        column.editableField as ConsiderationField,
                                        row[column.key]
                                    ) ?? null
                                }
                                section={ConsiderationSection.Summary}
                                rowKey={ledgerRow.considerationType}
                                editableFields={ledgerRow.editableFields}
                                fieldChanges={ledgerRow.fieldChanges}
                                engagementId={engagementId}
                                variant="plain"
                                className="cell-value"
                            />
                        ) : (
                            <AmountCell
                                value={row[column.key]}
                                feesNotApplicable={isProjectedFees && feesNotApplicable}
                                tooltipId={
                                    isProjectedFees && hasFeeBreakdown ? tooltipId : undefined
                                }
                            />
                        )}
                        {column.percentKey && (
                            <PercentCell
                                visible={visiblePercents[column.percentKey]}
                                value={row[column.key]}
                                total={total[column.key]}
                            />
                        )}
                    </React.Fragment>
                );
            })}
            {showTiming && <td className="cell-value">{timing}</td>}
            <td className="ff-approval__cell">
                {canEditRow && ledgerRow ? (
                    <ReadOnlyApproval approval={ledgerRow.buyerApproval} />
                ) : null}
            </td>
            <td className="ff-approval__cell">
                {canEditRow && ledgerRow ? (
                    <ReadOnlyApproval approval={ledgerRow.sellerApproval} />
                ) : null}
            </td>
        </tr>
    );
};

interface ConsiderationLedgerProps {
    consideration: ConsiderationLedgerModel;
    engagementId: number;
}

export const ConsiderationLedger = ({ consideration, engagementId }: ConsiderationLedgerProps) => {
    const { rows, total, detail, viewerParty } = consideration;
    const [collapsibleColumns, setCollapsibleColumns] = useState<Record<PercentKey, boolean>>({
        face: false,
        fair: false,
    });
    const autoSaveReporter = useConsiderationAutoSaveReporterState();
    const autosave = useConsiderationAutosave(consideration, engagementId, autoSaveReporter);

    const isBuyerView = viewerParty === TimelineParty.Buyer;
    const columns = isBuyerView ? BUYER_AMOUNT_COLUMNS : FULL_AMOUNT_COLUMNS;
    const showTiming = !isBuyerView;

    const toggleColumn = (key: PercentKey) =>
        setCollapsibleColumns((current) => ({
            ...current,
            [key]: !current[key],
        }));

    const rowLabel = (row: ConsiderationLedgerRow) => considerationTypeName(row.considerationType);

    return (
        <ConsiderationAutoSaveProvider reporter={autoSaveReporter}>
            <ConsiderationAutosaveQueueProvider queue={autosave}>
                <div className="ff-ledger">
                    <div className="loi-comparison-summary">
                        <div className="summary-inner">
                            <div className="loi-labels-column">
                                <table>
                                    <thead>
                                        <tr className="sub-col-header-row">
                                            <th className="col-label section-title">
                                                Consideration Summary
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr key={row.considerationType}>
                                                <td
                                                    className={`col-label${
                                                        row.isSubRow ? ' col-label--indented' : ''
                                                    }`}>
                                                    {rowLabel(row)}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="total-row">
                                            <td className="col-label">Total Value</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <table className="ff-ledger__data-table">
                                <thead>
                                    <tr className="sub-col-header-row">
                                        {columns.map((column) => (
                                            <React.Fragment key={column.key}>
                                                <th className="sub-col-header">{column.header}</th>
                                                {column.percentKey && (
                                                    <PercentToggleHeader
                                                        pctKey={column.percentKey}
                                                        visible={
                                                            collapsibleColumns[column.percentKey]
                                                        }
                                                        onToggle={toggleColumn}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                        {showTiming && (
                                            <th className="sub-col-header">Fee Payment Timing</th>
                                        )}
                                        <th className="sub-col-header">{BUYER_APPROVAL_LABEL}</th>
                                        <th className="sub-col-header">{SELLER_APPROVAL_LABEL}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const isHeaderOnly = isHeaderOnlyConsiderationType(
                                            row.considerationType
                                        );
                                        return (
                                            <LedgerDataRow
                                                key={row.considerationType}
                                                row={row}
                                                total={total}
                                                columns={columns}
                                                visiblePercents={collapsibleColumns}
                                                showTiming={showTiming}
                                                feesNotApplicable={row.feesNotApplicable}
                                                hasFeeBreakdown={row.hasFeeBreakdown}
                                                tooltipId={`ff-ledger-fee-tooltip-${row.considerationType}`}
                                                timing={
                                                    isHeaderOnly
                                                        ? ''
                                                        : considerationTreatmentName(
                                                              row.feePaymentTiming
                                                          )
                                                }
                                                ledgerRow={row}
                                                isHeaderOnly={isHeaderOnly}
                                                engagementId={engagementId}
                                            />
                                        );
                                    })}
                                    <LedgerDataRow
                                        row={total}
                                        total={total}
                                        columns={columns}
                                        visiblePercents={collapsibleColumns}
                                        showTiming={showTiming}
                                        hasFeeBreakdown
                                        tooltipId="ff-ledger-fee-tooltip-total"
                                        timing=""
                                        isTotal
                                        engagementId={engagementId}
                                    />
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <ConsiderationDetailTables
                        rows={rows}
                        detail={detail}
                        engagementId={engagementId}
                        viewerParty={viewerParty}
                        canAddRows={consideration.canAddRows}
                    />
                    <ToastContainer
                        containerId={CONSIDERATION_AUTOSAVE_TOAST_CONTAINER_ID}
                        position="bottom-right"
                        transition={Slide}
                        className="auto-save-toast-container"
                        newestOnTop={false}
                    />
                </div>
            </ConsiderationAutosaveQueueProvider>
        </ConsiderationAutoSaveProvider>
    );
};
